import { useCallback, useEffect, useState } from "react";

export interface RetryableLoad<T> {
  data: T;
  loading: boolean;
  error: boolean;
  retry: () => void;
}

/**
 * Loads once per `load` identity and again on `retry`. Data from the last
 * success stays while a reload runs or fails; late responses are dropped.
 * Pass a stable `load` (module function or useCallback) and `empty`.
 */
export function useRetryableLoad<T>(
  load: () => Promise<T>,
  empty: T,
): RetryableLoad<T> {
  const [data, setData] = useState<T>(empty);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(false);
    void load()
      .then((result) => {
        if (current) setData(result);
      })
      .catch(() => {
        if (current) setError(true);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [load, revision]);
  const retry = useCallback(() => setRevision((value) => value + 1), []);
  return { data, loading, error, retry };
}
